"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  CalendarClock,
  Check,
  type LucideIcon,
  Plug,
  Repeat,
  Ticket,
  Trophy,
  UserPlus,
  Zap,
} from "lucide-react";
import Image from "next/image";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { LogoMark } from "@/components/logo";
import { cn } from "@/lib/utils";

const EASE = [0.16, 1, 0.3, 1] as const;

/* ------------------------------------------------------------------ */
/* Reveal variants — pieces assemble inward around the centered copy.  */
/* ------------------------------------------------------------------ */

const group = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};
const fadeFromLeft = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: EASE } },
};
const fadeFromRight = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: EASE } },
};
const fade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: EASE } },
};

/* ------------------------------------------------------------------ */
/* Activation pulse — a trigger/playbook lights up as the signal hits. */
/* ------------------------------------------------------------------ */

const PULSE_DURATION = 0.9;

// Beam travel time. The choreography below keys off this so the trace dot, the
// engine pulse, and the playbook activation all land in sequence.
const BEAM_S = 1.2;
const BEAM_MS = BEAM_S * 1000;
// Matches the trigger/flow active span so the icon pulse runs for the same
// duration, with room for the one-shot ripple to fully bloom and dissolve
// before the trace heads back out — so each ripple is a discrete "boom".
const ENGINE_MS = BEAM_MS;
// The playbook stays lit for the same span the trigger does, so both sides read
// identically.
const LAND_MS = BEAM_MS;

const chipPulse = {
  idle: { scale: 1 },
  pulse: { scale: [1, 1.08, 1], transition: { duration: PULSE_DURATION, ease: EASE } },
};
const cardPulse = {
  idle: { scale: 1 },
  pulse: { scale: [1, 1.035, 1], transition: { duration: PULSE_DURATION, ease: EASE } },
};
// The engine mark reacts as the trace passes through it: the box gives a soft
// thump while a brand-accent overlay blooms once. Both key off the same label so
// the overlay (a child) inherits the parent's idle/pulse state.
const engineThump = {
  idle: { scale: 1 },
  // Matches the trigger/flow active span (BEAM_S) with a gentle ease-in-out so
  // the bloom ramps up and back down gradually rather than spiking.
  pulse: { scale: [1, 1.05, 1], transition: { duration: BEAM_S, ease: [0.4, 0, 0.2, 1] } },
};
const engineFlash = {
  idle: { opacity: 0 },
  // Gentle bloom — peaks well short of full strength so the accent ring reads as
  // a soft glow rather than a hard flash.
  pulse: { opacity: [0, 0.5, 0], transition: { duration: BEAM_S, ease: [0.4, 0, 0.2, 1] } },
};
// One-shot ripple: fires the instant the trace lands on the mark, blooms
// outward like a rock dropped in a pond, then dissolves. Not a loop — it stays
// silent until the next trace arrives. A trailing wave (delayed) gives the
// ripple body without ever repeating on its own.
const rippleOut = {
  idle: { opacity: 0, scale: 1 },
  pulse: {
    opacity: [0.5, 0],
    scale: [1, 1.55],
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  },
};
const rippleOutTrail = {
  idle: { opacity: 0, scale: 1 },
  pulse: {
    opacity: [0.32, 0],
    scale: [1, 1.32],
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.14 },
  },
};

/* ------------------------------------------------------------------ */
/* Content                                                            */
/* ------------------------------------------------------------------ */

const TOOLS = [
  { name: "Mariana Tek", src: "/images/integrations/mariana-tek/icon.png" },
  { name: "Mindbody", src: "/images/integrations/mindbody/icon.png" },
  { name: "fitDEGREE", src: "/images/integrations/fitdegree/icon.png" },
];

// The raw material flows feed on: events people do + segments they fall into.
// Ordered to map 1:1 with PLAYBOOKS below, so each signal lights up the flow
// it actually triggers.
const SIGNALS = [
  "Booked first class",
  "Intro offer ending",
  "No-showed",
  "Lapsed 30+ days",
  "Hit 100 classes",
];

type Playbook = { icon: LucideIcon; name: string; from: string; to: string };

// Audited lifecycle playbooks — one per stage, lead → loyal.
const PLAYBOOKS: Playbook[] = [
  { icon: UserPlus, name: "New-lead nurture", from: "New lead", to: "Booked" },
  { icon: Ticket, name: "Trial conversion", from: "Intro offer", to: "Member" },
  { icon: CalendarClock, name: "No-show recovery", from: "No-show", to: "Rebooked" },
  { icon: Repeat, name: "Win-back", from: "Lapsed", to: "Returned" },
  { icon: Trophy, name: "Milestone rewards", from: "100 classes", to: "Rewarded" },
];

/* ------------------------------------------------------------------ */
/* Building blocks                                                    */
/* ------------------------------------------------------------------ */

function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-background shadow-sm", className)}>
      {children}
    </div>
  );
}

function PanelHeader({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="size-3.5" />
      {children}
    </div>
  );
}

function ToolsCard() {
  return (
    <Panel className="w-full">
      <PanelHeader icon={Plug}>Your tools</PanelHeader>
      <div className="space-y-0.5 p-2">
        {TOOLS.map((tool) => (
          <div key={tool.name} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
            <Image
              src={tool.src}
              alt={tool.name}
              width={20}
              height={20}
              className="size-5 shrink-0 rounded-md object-contain"
            />
            <span className="text-[12px] font-medium text-foreground">{tool.name}</span>
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-success">
              <Check className="size-3" />
              Synced
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function SignalChip({ label, active }: { label: string; active: boolean }) {
  return (
    <motion.span
      variants={chipPulse}
      animate={active ? "pulse" : "idle"}
      className={cn(
        "relative inline-flex rounded-full border px-2 py-1 text-[11px] font-medium transition-colors duration-300 [will-change:transform]",
        active
          ? "border-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent)/0.12)] text-foreground"
          : "border-border bg-muted/40 text-foreground",
      )}
    >
      {label}
    </motion.span>
  );
}

function SignalsCard({ activeIndex = -1 }: { activeIndex?: number }) {
  return (
    <Panel className="w-full">
      <PanelHeader icon={Zap}>Triggers &amp; segments</PanelHeader>
      <div className="flex flex-wrap gap-1.5 p-3">
        {SIGNALS.map((signal, i) => (
          <SignalChip key={signal} label={signal} active={i === activeIndex} />
        ))}
      </div>
    </Panel>
  );
}

function PlaybookCard({ playbook, active = false }: { playbook: Playbook; active?: boolean }) {
  const Icon = playbook.icon;
  return (
    <motion.div
      variants={cardPulse}
      animate={active ? "pulse" : "idle"}
      className={cn(
        "relative flex items-center gap-3 rounded-xl border px-3.5 py-3 shadow-sm transition-colors duration-300 [will-change:transform]",
        active
          ? "border-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent)/0.12)]"
          : "border-border bg-background",
      )}
    >
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg border transition-colors duration-300",
          active
            ? "border-[hsl(var(--brand-accent)/0.4)] bg-[hsl(var(--brand-accent)/0.06)] text-foreground"
            : "border-border bg-muted/50 text-muted-foreground",
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-foreground">{playbook.name}</p>
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
          <span className="truncate">{playbook.from}</span>
          <ArrowRight className="size-3 shrink-0" />
          <span className="truncate">{playbook.to}</span>
        </p>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
        <span className="size-1.5 animate-pulse rounded-full bg-success" />
        Live
      </span>
    </motion.div>
  );
}

function EngineMark({ reduce, active }: { reduce: boolean; active: boolean }) {
  return (
    <motion.div
      variants={engineThump}
      animate={!reduce && active ? "pulse" : "idle"}
      className="relative flex size-16 items-center justify-center rounded-2xl border border-border bg-background shadow-sm [will-change:transform]"
    >
      <LogoMark className="relative z-10 size-7" />
      {reduce ? null : (
        <>
          {/* One-shot ripple, fired only when the trace lands (engine phase).
              Born at the mark, races outward, decelerates and fades — then
              nothing until the next trace arrives. Children inherit the
              parent's idle/pulse label. */}
          <motion.span
            aria-hidden
            variants={rippleOut}
            className="pointer-events-none absolute inset-0 rounded-2xl border border-[hsl(var(--brand-accent)/0.7)] [transform:translateZ(0)] [will-change:transform,opacity]"
          />
          <motion.span
            aria-hidden
            variants={rippleOutTrail}
            className="pointer-events-none absolute inset-0 rounded-2xl border border-[hsl(var(--brand-accent)/0.6)] [transform:translateZ(0)] [will-change:transform,opacity]"
          />
          {/* Trace-arrival flash: a brand-accent ring that blooms once on the
              mark's edge as the inbound signal reaches the engine — the "boom"
              at the center of the ripple, no background fill. */}
          <motion.span
            aria-hidden
            variants={engineFlash}
            className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-inset ring-[hsl(var(--brand-accent))] [will-change:opacity]"
          />
        </>
      )}
    </motion.div>
  );
}

// Horizontal rail that fires a single pulse left → right when `active` flips
// true, syncing with the trigger/playbook that lights up on either end. The dot
// rides a GPU transform (translateX) so it stays on the compositor — fully
// fluid, no layout thrash.
function FlowBeam({
  reduce,
  active,
  className,
}: {
  reduce: boolean;
  active: boolean;
  className?: string;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const measure = () => setWidth(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <motion.div
      ref={railRef}
      aria-hidden
      variants={fade}
      className={cn("relative h-px shrink-0 bg-border", className)}
    >
      {reduce || width === 0 || !active ? null : (
        <motion.span
          className="absolute left-0 top-1/2 size-1.5 rounded-full bg-[hsl(var(--brand-accent))] shadow-[0_0_6px_1px_hsl(var(--brand-accent)/0.6)] [will-change:transform,opacity]"
          style={{ marginTop: -3 }}
          initial={{ x: 0, opacity: 0 }}
          animate={{ x: [0, width * 0.15, width * 0.85, width], opacity: [0, 1, 1, 0] }}
          transition={{ duration: BEAM_S, ease: "linear", times: [0, 0.15, 0.85, 1] }}
        />
      )}
    </motion.div>
  );
}

function CenterCopy({ reduce, enginePulse }: { reduce: boolean; enginePulse: boolean }) {
  return (
    <motion.div variants={group} className="flex flex-col items-center text-center">
      <motion.div variants={fadeUp}>
        <EngineMark reduce={reduce} active={enginePulse} />
      </motion.div>
      <motion.span
        variants={fadeUp}
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
      >
        The Helios Engine
      </motion.span>
      <motion.h2
        variants={fadeUp}
        className="mt-3 text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
      >
        Every signal becomes the right journey.
      </motion.h2>
      <motion.p
        variants={fadeUp}
        className="mt-4 max-w-sm text-balance text-sm leading-relaxed text-muted-foreground"
      >
        Helios unifies the events and segments from the tools you already run, then automatically
        routes each person into the playbook that moves them forward.
      </motion.p>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

export function FlowsEngine() {
  const reduce = useReducedMotion() ?? false;
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { amount: 0.3 });
  const count = PLAYBOOKS.length;
  const [activeIdx, setActiveIdx] = useState(0);
  const [phase, setPhase] = useState<"in" | "engine" | "out" | "land">("in");

  // One fully sequenced cycle per signal:
  //   in     → the trigger lights and the trace travels into the engine
  //   engine → the trace lands; the Helios mark pulses brand accent
  //   out    → the trace travels from the engine toward its playbook
  //   land   → the trace arrives; only now does the playbook light up
  useEffect(() => {
    if (reduce || !inView) return;
    const steps: Array<[typeof phase, number]> = [
      ["in", BEAM_MS],
      ["engine", ENGINE_MS],
      ["out", BEAM_MS],
      ["land", LAND_MS],
    ];
    let cancelled = false;
    let idx = 0;
    let si = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (cancelled) return;
      const [ph, dur] = steps[si];
      setActiveIdx(idx);
      setPhase(ph);
      timer = setTimeout(() => {
        si += 1;
        if (si >= steps.length) {
          si = 0;
          idx = (idx + 1) % count;
        }
        tick();
      }, dur);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [reduce, inView, count]);

  // Trigger and playbook light identically: the trigger while the trace travels
  // in, the playbook once it has landed — each for the same span (BEAM_MS).
  const activeSignal = phase === "in" ? activeIdx : -1;
  const activePlaybook = phase === "land" ? activeIdx : -1;
  const leftBeam = phase === "in";
  const rightBeam = phase === "out";
  const enginePulseActive = phase === "engine";

  const motionProps = reduce
    ? { initial: false as const, animate: "visible" as const }
    : {
        initial: "hidden" as const,
        whileInView: "visible" as const,
        viewport: { once: true, margin: "-80px" },
      };

  return (
    <section ref={sectionRef} className="border-b border-border bg-muted/30 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* ---------------------------------------------------------- */}
        {/* Desktop: inputs ← copy → playbooks, surrounded composition  */}
        {/* ---------------------------------------------------------- */}
        <motion.div
          {...motionProps}
          variants={group}
          className="hidden items-center gap-5 lg:flex xl:gap-7"
        >
          {/* Data in — your tools + the signals they emit */}
          <motion.div variants={group} className="flex flex-1 flex-col items-end gap-4">
            <motion.div variants={fadeFromLeft} className="w-full max-w-[260px]">
              <ToolsCard />
            </motion.div>
            <motion.div variants={fadeFromLeft} className="w-full max-w-[260px]">
              <SignalsCard activeIndex={activeSignal} />
            </motion.div>
          </motion.div>

          <FlowBeam reduce={reduce} active={leftBeam} className="w-10 xl:w-14" />

          {/* The engine — centered copy */}
          <div className="w-[320px] shrink-0 xl:w-[360px]">
            <CenterCopy reduce={reduce} enginePulse={enginePulseActive} />
          </div>

          <FlowBeam reduce={reduce} active={rightBeam} className="w-10 xl:w-14" />

          {/* Journeys out — the live playbooks */}
          <motion.div variants={group} className="flex flex-1 flex-col items-start gap-2.5">
            {PLAYBOOKS.map((playbook, i) => (
              <motion.div
                key={playbook.name}
                variants={fadeFromRight}
                className="w-full max-w-[280px]"
              >
                <PlaybookCard playbook={playbook} active={i === activePlaybook} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* ---------------------------------------------------------- */}
        {/* Mobile / tablet: linear story, copy → inputs → playbooks    */}
        {/* ---------------------------------------------------------- */}
        <motion.div {...motionProps} variants={group} className="flex flex-col gap-10 lg:hidden">
          <CenterCopy reduce={reduce} enginePulse={enginePulseActive} />

          <motion.div variants={group} className="flex flex-col gap-6">
            <div>
              <motion.p
                variants={fadeUp}
                className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Your data feeds in
              </motion.p>
              <motion.div variants={fadeUp} className="grid gap-3 sm:grid-cols-2">
                <ToolsCard />
                <SignalsCard activeIndex={activeSignal} />
              </motion.div>
            </div>

            <motion.div variants={fade} className="flex items-center justify-center" aria-hidden>
              <span className="flex size-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground">
                <ArrowRight className="size-4 rotate-90" />
              </span>
            </motion.div>

            <div>
              <motion.p
                variants={fadeUp}
                className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Playbooks run automatically
              </motion.p>
              <motion.div variants={group} className="grid gap-2.5 sm:grid-cols-2">
                {PLAYBOOKS.map((playbook, i) => (
                  <motion.div key={playbook.name} variants={fadeUp}>
                    <PlaybookCard playbook={playbook} active={i === activePlaybook} />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
